// Lib
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@milanote/roma';

// Utils
import http from '../../utils/services/http';

// Constants
import { METHODS } from '../../../common/utils/http/httpConstants';

// Components
import AdminDashboardTimestamp from '../components/AdminDashboardTimestamp';
import { AdminPage, MessageBox, DataTable } from '@milanote/roma/admin-dashboard';
import AdminDashboardModal, { Title as ModalTitle } from '../components/AdminDashboardModal';
import AppVersionForm from './AdminDashboardAppVersionsForm';

const ENDPOINT_URL = 'admin/app-version-rules/rules';

const AdminDashboardAppVersions = () => {
    const [showAddRuleForm, setShowAddRuleForm] = useState(false);
    const [rules, setRules] = useState([]);

    const fetchRules = useCallback(async () => {
        const { data } = await http({
            method: METHODS.GET,
            url: ENDPOINT_URL,
        });

        setRules(data.rules);
    }, []);

    const removeRule = async (ruleId) => {
        await http({
            method: METHODS.DELETE,
            url: ENDPOINT_URL,
            params: {
                id: ruleId,
            },
        });

        await fetchRules();
    };

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    return (
        <AdminPage className="AdminDashboardAppVersions">
            <AdminPage.Header>
                <AdminPage.Header.Main>
                    <AdminPage.Header.Title>App Version Rules</AdminPage.Header.Title>
                </AdminPage.Header.Main>

                <AdminPage.Header.Actions>
                    <Button color="secondary" size="sm" onClick={() => setShowAddRuleForm(true)}>
                        Add rule
                    </Button>
                </AdminPage.Header.Actions>
            </AdminPage.Header>

            <MessageBox>
                This is a list of rules for blacklisting app versions.
                <br />
                The rules dictate which versions are BLOCKED. e.g. the rule &quot;ios &lt;= 3.0.0&quot; means versions
                older than 3.0.0 are blocked.
                <br />
                <br />
                The actual blocking happens client side. The client hits an endpoint that tells it if its version is
                blocked.
            </MessageBox>

            <AdminDashboardModal isOpen={showAddRuleForm} closeModal={() => setShowAddRuleForm(false)}>
                <div className="flex flex-col gap-lg">
                    <ModalTitle>Add app version rule</ModalTitle>
                    <AppVersionForm
                        url={ENDPOINT_URL}
                        onComplete={() => {
                            fetchRules();
                            setShowAddRuleForm(false);
                        }}
                    />
                </div>
            </AdminDashboardModal>

            <DataTable>
                <DataTable.Header>
                    <DataTable.Head isRowHeader>Platform</DataTable.Head>
                    <DataTable.Head>Modifier</DataTable.Head>
                    <DataTable.Head>Version</DataTable.Head>
                    <DataTable.Head>Status</DataTable.Head>
                    <DataTable.Head>Reason</DataTable.Head>
                    <DataTable.Head>Added By</DataTable.Head>
                    <DataTable.Head>Added At</DataTable.Head>
                    <DataTable.Head aria-label="Action" />
                </DataTable.Header>
                <DataTable.Body>
                    {rules.map(({ id, platform, modifier, version, reason, addedBy, addedAt, status }) => (
                        <DataTable.Row key={`${platform}${modifier}${version}${status}`}>
                            <DataTable.Cell>{platform}</DataTable.Cell>
                            <DataTable.Cell>{modifier}</DataTable.Cell>
                            <DataTable.Cell>{version}</DataTable.Cell>
                            <DataTable.Cell>{status}</DataTable.Cell>
                            <DataTable.Cell>{reason}</DataTable.Cell>
                            <DataTable.Cell>{addedBy}</DataTable.Cell>
                            <DataTable.Cell>
                                <AdminDashboardTimestamp timestamp={addedAt} />
                            </DataTable.Cell>
                            <DataTable.Cell className="text-right">
                                <Button color="link-gray" onClick={() => removeRule(id)}>
                                    Remove rule
                                </Button>
                            </DataTable.Cell>
                        </DataTable.Row>
                    ))}
                </DataTable.Body>
            </DataTable>
        </AdminPage>
    );
};

export default AdminDashboardAppVersions;
